import { useCallback, useEffect, useRef, useState } from "react";
import type {
  StripeElement as StripeElementObj,
  StripeElementChangeEvent,
  StripeElements,
  StripeElementType,
} from "@stripe/stripe-js";
import useCallbackRef from "@shared/hooks/useCallbackRef/useCallbackRef";
// eslint-disable-next-line import/no-cycle
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import { useColorMode } from "@shared/hooks/useColorMode";
import {
  buildCardElementStyle,
  buildMessagingAppearance,
  resolveStripeColorMode,
} from "../../utils/stripe/stripe-theme-utils";
// eslint-disable-next-line import/no-cycle
import StripePaymentGateway from "../../gateways/StripePaymentGateway/StripePaymentGateway";
import { createPaymentLogger } from "../../utils/payment-logger";

const log = createPaymentLogger("StripeElement");

/**
 * A containing element for Stripe.JS to inject their elements into
 * Accepts an argument element which tells Stripe Elements what kind of element to create,
 * and exposes a callback function API `onChange` which attaches to the element's `change` Stripe Event.
 */
function StripeElement({
  element,
  elementProps,
  elementUpdateProps,
  onChange,
  onElementCreate,
  alwaysCreateFresh = false,
  cleanupStrategy = "unmount",
}: StripeElementProps) {
  const { manager } = usePaymentManager();
  const { colorMode } = useColorMode();
  const domElementRef = useRef<HTMLDivElement>(null);

  const stripeElementRef = useRef<StripeElementObj | null>();
  // Separate elements instance created for paymentMethodMessaging so its
  // appearance can be live-updated independently from the shared instance.
  const messagingElementsRef = useRef<StripeElements | null>(null);

  const isCardElement =
    element === "cardNumber" ||
    element === "cardExpiry" ||
    element === "cardCvc";

  const onChangeRef = useCallbackRef(onChange);
  const onElementCreateRef = useCallbackRef(onElementCreate);
  const hasCalledCreateAndMount = useRef(false);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const gateway = manager.handler?.gateway;
  const stripeGateway =
    gateway instanceof StripePaymentGateway ? gateway : undefined;
  const elementsReady = stripeGateway?.elementsInstance;

  const createAndMountElement = useCallback(() => {
    if (hasCalledCreateAndMount.current) return;
    if (!stripeGateway?.elementsInstance) return;
    if (!domElementRef.current) return;

    try {
      hasCalledCreateAndMount.current = true;

      if (!alwaysCreateFresh) {
        stripeElementRef.current = stripeGateway.elementsInstance.getElement(
          // TS is not understanding Stripe's `create` overloads properly.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error - StripeElement is not typed correctly
          element
        );
      }

      if (!stripeElementRef.current) {
        const options = { ...elementProps };
        if (isCardElement) {
          options.style = buildCardElementStyle(resolveStripeColorMode());
        }

        if (element === "paymentMethodMessaging") {
          const messagingElements = stripeGateway.instance?.elements({
            appearance: buildMessagingAppearance(resolveStripeColorMode()),
          });

          if (!messagingElements) return;
          messagingElementsRef.current = messagingElements;
          stripeElementRef.current = messagingElements.create(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error - StripeElement is not typed correctly
            element,
            options
          );
        } else {
          stripeElementRef.current = stripeGateway.elementsInstance.create(
            // TS is not understanding Stripe's `create` overloads properly.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error - StripeElement is not typed correctly
            element,
            options
          );
        }
      }

      if (!stripeElementRef.current) return;

      stripeElementRef.current.mount(domElementRef.current);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - StripeElement is not typed correctly
      stripeElementRef.current.on(
        "change",
        (...args: [StripeElementChangeEvent]) => onChangeRef.current?.(...args)
      );

      onElementCreateRef.current?.(stripeElementRef.current);
    } catch (e) {
      hasCalledCreateAndMount.current = false;
      stripeElementRef.current = null;

      if (
        alwaysCreateFresh &&
        e instanceof Error &&
        e.message?.includes("Can only create one Element")
      ) {
        retryTimerRef.current = setTimeout(
          () => setRetryCount((c) => c + 1),
          500
        );
      }
      log.error("Failed to create/mount Stripe element", {
        error: e instanceof Error ? e.message : String(e),
        element,
      });
    }
  }, [stripeGateway, elementsReady, element, alwaysCreateFresh]);

  useEffect(() => {
    createAndMountElement();
  }, [createAndMountElement, retryCount]);

  // Live-update element styles when the color mode changes.
  // Card elements use the legacy style API; messaging uses its own elements instance.
  // Uses colorMode from React state rather than reading the DOM class
  // (isDarkMode), because child effects run before ThemeProvider applies
  // the new class to <html>.
  const prevColorModeRef = useRef(colorMode);
  useEffect(() => {
    if (prevColorModeRef.current === colorMode) return;
    prevColorModeRef.current = colorMode;

    if (
      isCardElement &&
      typeof stripeElementRef.current?.update === "function"
    ) {
      stripeElementRef.current.update({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error -- style type requires paymentRequestButton which card elements don't use
        style: buildCardElementStyle(colorMode),
      });
    }

    if (element === "paymentMethodMessaging" && messagingElementsRef.current) {
      messagingElementsRef.current.update({
        appearance: buildMessagingAppearance(colorMode),
      });
    }
  }, [colorMode, isCardElement, element]);

  // Sync prop changes to an already-mounted element via update().
  // Prefers elementUpdateProps (which excludes creation-only fields like
  // paymentMethods, buttonType, shippingRates) over the full elementProps.
  const propsToSync = elementUpdateProps ?? elementProps;
  const prevElementPropsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!stripeElementRef.current) return;
    if (!propsToSync) return;
    if (!hasCalledCreateAndMount.current) return;

    const json = JSON.stringify(propsToSync);

    if (!prevElementPropsRef.current) {
      prevElementPropsRef.current = json;
      return;
    }

    if (prevElementPropsRef.current === json) return;
    prevElementPropsRef.current = json;

    if (typeof stripeElementRef.current.update === "function") {
      stripeElementRef.current.update(propsToSync);
    }
  });

  useEffect(() => {
    return () => {
      clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const strategy = cleanupStrategy;
    return () => {
      if (strategy === "destroy") {
        stripeElementRef.current?.destroy();
        stripeElementRef.current = null;
      } else {
        stripeElementRef.current?.unmount();
      }
    };
  }, [cleanupStrategy]);

  return (
    <>
      <style>{`
        /* Override auto-fill background color */
        .StripeElement--webkit-autofill {
          background-color: transparent !important;
        }
      `}</style>
      <div ref={domElementRef} data-testid={`stripe-element-${element}`} />
    </>
  );
}

export default StripeElement;

export type StripeElementProps = {
  element: StripeElementType;
  /** Options passed to element.create(). Used for initial creation. */
  elementProps?: Record<string, unknown>;
  /** Subset of options safe to pass to element.update(). When provided, the
   *  sync effect uses these instead of elementProps to avoid Stripe warnings
   *  about creation-only fields (e.g. paymentMethods, buttonType). */
  elementUpdateProps?: Record<string, unknown>;
  onChange?: (e: StripeElementChangeEvent) => void;
  onElementCreate?: (element: StripeElementObj) => void;
  /** Skip getElement() reuse and always create a fresh element. */
  alwaysCreateFresh?: boolean;
  /** "destroy" fully removes the element; "unmount" detaches but keeps it in Stripe's registry. */
  cleanupStrategy?: "unmount" | "destroy";
};
